import { Fragment } from 'react';
import { IconChevronDown, IconChevronUp, IconMinus } from '@tabler/icons-react';
import type { CustomCellRendererProps } from 'ag-grid-react';
import { isNil } from 'lodash';

import { Tooltip, TooltipBody, PillSuggestion } from '@wk/components-react16';

import { ORR_PLATFORMS, ENVIRONMENT_ACRONYMS } from '@/config/constants';
import { AutomationPlatform, type Evaluation } from '@/types';
import { cn } from '@/lib/utils';

export const renderApplicationName = (
  params: CustomCellRendererProps<Evaluation>,
) => {
  const { data } = params;
  if (!data) return null;
  const id = `application-${data.id.replace(/[^a-zA-Z0-9\s]/g, '')}`;
  return (
    <span className="flex items-center gap-3">
      <span className="max-w-11/12 truncate">{data.applicationName}</span>
      <span className="flex gap-1">
        {data.environmentNames.map((env) => (
          <Fragment key={env}>
            <Tooltip
              position="right"
              targetSelector={`#${id}-env-${env.toLowerCase()}`}
            >
              <TooltipBody slot="tooltipBody">{env}</TooltipBody>
            </Tooltip>
            <span
              id={`${id}-env-${env.toLowerCase()}`}
              className={cn(['envBadge', `envBadge--${env.toLowerCase()}`])}
            >
              <PillSuggestion size="small" key={env}>
                {
                  ENVIRONMENT_ACRONYMS[
                    env.toLowerCase() as keyof typeof ENVIRONMENT_ACRONYMS
                  ]
                }
              </PillSuggestion>
            </span>
          </Fragment>
        ))}
      </span>
    </span>
  );
};

export const renderSource = (
  params: CustomCellRendererProps<Evaluation>,
  { renderName = false }: { renderName?: boolean } = {},
) => {
  const { data } = params;
  if (!data) return null;

  const schedulerPlatform = AutomationPlatform.SCHEDULER as unknown as string;
  const headerPlatform =
    data.request?.headers?.['x-orr-automation-platform'] as unknown as string;

  const isScheduled =
    data.platform === schedulerPlatform ||
    data.generatedBy === schedulerPlatform ||
    headerPlatform === schedulerPlatform;

  const resolvedPlatform = [headerPlatform, data.platform as unknown as string].find(
    (platform) =>
      Boolean(platform) &&
      Object.prototype.hasOwnProperty.call(ORR_PLATFORMS, platform),
  ) as AutomationPlatform | undefined;

  const effectivePlatform = isScheduled
    ? AutomationPlatform.SCHEDULER
    : (resolvedPlatform ?? AutomationPlatform.UNKNOWN);

  const platform =
    ORR_PLATFORMS[effectivePlatform] ||
    ORR_PLATFORMS[AutomationPlatform.UNKNOWN];

  const Icon = platform.icon;
  const label = platform.label;
  const id = `source-${data.id.replace(/[^a-zA-Z0-9\s]/g, '')}`;
  return (
    <span className="flex items-center gap-2">
      {!renderName && (
        <Tooltip position="right" targetSelector={`#${id}`}>
          <TooltipBody slot="tooltipBody">{label}</TooltipBody>
        </Tooltip>
      )}
      <span id={`${id}`} className="flex items-center gap-2">
        <span className="w-5 h-5">
          <Icon className="w-full h-full" />
        </span>
        {renderName && <span>{label}</span>}
      </span>
    </span>
  );
};

export const renderScore = (params: CustomCellRendererProps<Evaluation>) => {
  const { data, column } = params;
  const colId = column?.getColId();
  const isUpcoming = colId === 'inclusiveScore';
  if (!data?.totalEvaluations)
    return <IconMinus size={14} className="text-gray-500" />;
  if (isNil(data?.score)) return <span>N/A</span>;

  if (isUpcoming && isNil(data?.inclusiveScore))
    return <IconMinus size={14} className="text-gray-500" />;

  const renderScoreDirection = (_score: number) => {
    if (!data?.previous || isNil(data?.previous.score)) return null;
    if (_score > data.previous.score)
      return <IconChevronUp size={14} className="text-green-500" />;
    if (_score === data.previous.score)
      return <IconMinus size={14} className="text-gray-500" />;
    return <IconChevronDown size={14} className="text-red-400" />;
  };

  return (
    <span className="flex flex-col gap-1">
      <span className="flex items-center gap-2">
        {isUpcoming ? data.inclusiveScore : data.score}%
        {renderScoreDirection(isUpcoming ? data.inclusiveScore : data.score)}
      </span>
      <span className="text-[10px] text-gray-500 dark:text-gray-400">
        {isUpcoming ? data.totalCheckpoints : data.totalScoredCheckpoints}{' '}
        Checkpoints
      </span>
    </span>
  );
};
